import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { MdArrowBackIosNew, MdArrowForwardIos, MdClose } from "react-icons/md";

const ProjectDetail = () => {
  const { id } = useParams();
  const { t } = useTranslation(["projects", "translation"]);

  // 1. RECUPERO DATI E NAVIGAZIONE (Senza Warning)
  const projectsData = useMemo(() => {
    const data = t("projects", { ns: "projects", returnObjects: true });
    return Array.isArray(data) ? data : [];
  }, [t]);

  const project = useMemo(() => {
    return projectsData.find((p) => String(p.id) === String(id));
  }, [projectsData, id]);

  const { prevProject, nextProject, otherProjects } = useMemo(() => {
    const index = projectsData.findIndex((p) => String(p.id) === String(id));
    return {
      prevProject: projectsData[index - 1] || null,
      nextProject: projectsData[index + 1] || null,
      otherProjects: projectsData.filter((p) => String(p.id) !== String(id)).slice(0, 3),
    };
  }, [projectsData, id]);

  // 2. STATO MODALE
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);

  // 3. FUNZIONI MODALE (Ottimizzate con useCallback)
  const closeModal = useCallback(() => {
    setModalOpen(false);
    document.body.style.overflow = "unset";
    if (window.history.state?.modalOpen) {
      window.history.replaceState(null, "");
    }
  }, []);

  const openModal = useCallback((index) => {
    setSelectedImageIndex(index);
    setModalOpen(true);
    document.body.style.overflow = "hidden";
    window.history.pushState({ modalOpen: true }, "");
  }, []);

  const nextImage = useCallback(
    (e) => {
      e?.stopPropagation();
      if (!project) return;
      setSelectedImageIndex((prev) => (prev === project.galleryImages.length - 1 ? 0 : prev + 1));
    },
    [project]
  );

  const prevImage = useCallback(
    (e) => {
      e?.stopPropagation();
      if (!project) return;
      setSelectedImageIndex((prev) => (prev === 0 ? project.galleryImages.length - 1 : prev - 1));
    },
    [project]
  );

  // 4. GESTIONE EVENTI (Browser e Tastiera)
  useEffect(() => {
    const handlePopState = () => {
      if (modalOpen) {
        setModalOpen(false);
        document.body.style.overflow = "unset";
      }
    };

    const handleKeyDown = (e) => {
      if (!modalOpen) return;
      if (e.key === "Escape") closeModal();
      if (e.key === "ArrowRight") nextImage();
      if (e.key === "ArrowLeft") prevImage();
    };

    window.addEventListener("popstate", handlePopState);
    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("popstate", handlePopState);
      window.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = "unset";
    };
  }, [modalOpen, closeModal, nextImage, prevImage]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [id]);

  if (!project) return null;

  return (
    <div className="text-white max-w-6xl mx-auto py-12 px-4 relative z-10">
      {/* Header */}
      <div className="text-center mb-16 md:mb-24">
        <h1 className="text-3xl md:text-5xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-purple-400 mt-20 py-2">
          {project.title}
        </h1>
      </div>

      {/* Main Info */}
      <div className="flex flex-col md:flex-row gap-12 items-start">
        <div className="w-full md:w-1/2">
          <img
            src={project.image}
            alt={project.title}
            className="w-full h-auto rounded-lg shadow-2xl border border-white/10"
          />
          <div className="mt-6 space-y-3">
            <p className="text-lg">
              <strong className="text-purple-300">{t("translation:projects.year")}:</strong> {project.year}
            </p>
            <p className="text-lg">
              <strong className="text-blue-300">{t("translation:projects.activities")}:</strong> {project.activities}
            </p>
          </div>
        </div>
        <div className="w-full md:w-1/2 text-gray-300">
          <p className="text-base sm:text-base md:text-lg text-gray-200 leading-relaxed">
            {project.longDescription || project.description}
          </p>
          {project.link && project.link !== "#" && (
            <div className="mt-10">
              <a
                href={project.link}
                target="_blank"
                rel="noopener noreferrer"
                className="bg-white text-black font-bold py-3 px-8 rounded hover:scale-105 transition-transform inline-block"
              >
                {t("translation:projects.visit")}
              </a>
            </div>
          )}
        </div>
      </div>

      {/* Gallery */}
      {project.galleryImages?.length > 0 && (
        <div className="mt-24">
          <h2 className="text-2xl font-bold mb-10 text-center text-blue-300 uppercase tracking-widest">
            {t("translation:projects.gallery")}
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
            {project.galleryImages.map((img, i) => (
              <div
                key={i}
                onClick={() => openModal(i)}
                className="group relative cursor-pointer overflow-hidden rounded-xl border border-white/10 bg-black/20"
              >
                <img
                  src={img}
                  alt=""
                  className="w-full aspect-video object-cover transition-opacity duration-500 group-hover:opacity-60"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 flex items-end justify-center pb-16">
                  <div className="flex flex-col items-center gap-3 translate-y-4 group-hover:translate-y-0 transition-all duration-500">
                    <span className="text-white text-xs md:text-sm font-bold uppercase tracking-[0.3em] drop-shadow-xl">
                      {t("translation:projects.view")}
                    </span>
                    <div className="w-28 h-[2px] bg-gradient-to-r from-transparent via-purple-400 to-transparent"></div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Project Navigation */}
      <div className="mt-20 pt-10 border-t border-white/10 flex justify-between items-center font-semibold text-purple-400">
        {prevProject ? (
          <Link
            to={`/progetti/${prevProject.id}`}
            className="hover:text-purple-300 flex items-center gap-2 transition-colors"
          >
            <MdArrowBackIosNew /> {t("translation:projects.previous")}
          </Link>
        ) : (
          <div />
        )}
        {nextProject ? (
          <Link
            to={`/progetti/${nextProject.id}`}
            className="hover:text-purple-300 flex items-center gap-2 transition-colors"
          >
            {t("translation:projects.next")} <MdArrowForwardIos />
          </Link>
        ) : (
          <div />
        )}
      </div>

      {/* Suggested Projects */}
      {otherProjects.length > 0 && (
        <div className="mt-32">
          <h2 className="text-2xl font-bold mb-12 text-center text-blue-300">
            {t("translation:projects.otherProjects")}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {otherProjects.map((op) => (
              <Link
                key={op.id}
                to={`/progetti/${op.id}`}
                className="group bg-gray-900/50 rounded-xl overflow-hidden border border-white/10 hover:border-purple-500 transition-all duration-300 hover:-translate-y-2"
              >
                <div className="aspect-video overflow-hidden">
                  <img
                    src={op.image}
                    alt={op.title}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-bold text-white group-hover:text-purple-400 transition-colors">
                    {op.title}
                  </h3>
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* MODAL */}
      {modalOpen && (
        <div
          className="fixed inset-0 bg-black/90 z-[9999] flex items-center justify-center p-4 select-none"
          onClick={closeModal}
        >
          <button
            className="absolute top-28 right-6 md:right-12 text-white bg-white/10 hover:bg-white/20 backdrop-blur-md p-2.5 rounded-full z-[10000] border border-white/10 active:scale-90 transition-all shadow-xl"
            onClick={(e) => {
              e.stopPropagation();
              closeModal();
            }}
          >
            <MdClose size={28} />
          </button>

          <button
            className="absolute left-2 md:left-10 text-white/40 hover:text-white text-5xl z-[10000] p-4"
            onClick={prevImage}
          >
            <MdArrowBackIosNew />
          </button>

          <img
            src={project.galleryImages[selectedImageIndex]}
            className="max-w-full max-h-[70vh] md:max-h-[85vh] object-contain shadow-2xl rounded-sm"
            onClick={(e) => e.stopPropagation()}
            alt="Fullsize"
          />

          <button
            className="absolute right-2 md:right-10 text-white/40 hover:text-white text-5xl z-[10000] p-4"
            onClick={nextImage}
          >
            <MdArrowForwardIos />
          </button>

          <div className="absolute bottom-10 text-white/60 bg-white/5 px-6 py-2 rounded-full backdrop-blur-md text-xs tracking-widest border border-white/5">
            {selectedImageIndex + 1} / {project.galleryImages.length}
          </div>
        </div>
      )}
    </div>
  );
};

export default ProjectDetail;
